import { IEntity } from '@opengov/og-api-ts';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { AddTableTileHandle } from '../../../../../../modals/components/AddTableTile/AddTableTile';
import { AddTableTileWithRefContainer } from '../../../../../../modals/components/AddTableTile/AddTableTileContainer';
import { removeUuidFromEditables } from '../../../../../../stories/utils/dom';
import { IModalMessages, ISessionUser } from '../../../../../../types';
import { assertNotNil } from '../../../../../../types/typeUtils';
import { ComponentErrorBoundary } from '../../../../ErrorBoundary/ComponentErrorBoundary';
import { ErrorFallback } from '../../../../ErrorBoundary/Fallbacks/ErrorFallback';
import { ReportTableHandle } from '../AddReportTableModal.types';

type Props = {
  story: IStory;
  storyContents: IStoryContent[];
  entityId: number;
  entitySubdomain: string;
  entity: IEntity;
  user: ISessionUser;
  messages: IModalMessages;
  isTemplate: boolean;
  onFormDataChange: (isValid: boolean) => void;
  onFormLoadingChange?: (isLoading: boolean) => void;
  storyContent: IStoryContent;
  mediaIndex: number | null;
};

export const AddOpenGovReport = forwardRef<ReportTableHandle, Props>(
  (
    {
      story,
      storyContents,
      entityId,
      entitySubdomain,
      entity,
      user,
      messages,
      isTemplate,
      onFormDataChange,
      onFormLoadingChange,
      storyContent,
      mediaIndex
    },
    ref
  ) => {
    const addTableTileRef = useRef<AddTableTileHandle | null>(null);

    useImperativeHandle(ref, () => ({
      handleSubmit: async () => {
        const addTableTile = assertNotNil(addTableTileRef.current);
        await addTableTile.handleAdd();
      }
    }));

    const cleanedStoryContent = {
      ...storyContent,
      content: removeUuidFromEditables(storyContent.content),
      mediaIndex: mediaIndex ?? 0
    };

    return (
      <ComponentErrorBoundary FallbackComponent={ErrorFallback}>
        <AddTableTileWithRefContainer
          isTemplate={isTemplate}
          entityId={entityId}
          entitySubdomain={entitySubdomain}
          entity={entity}
          user={user}
          story={story}
          storyContents={storyContents}
          messages={messages}
          storyContent={cleanedStoryContent}
          selectedEmbed={null}
          isReportEmbedListSelected={false}
          onClose={() => {}}
          shouldOnlyRenderInnerContent={true}
          onFormDataChange={onFormDataChange}
          onFormLoadingChange={onFormLoadingChange}
          ref={addTableTileRef}
        />
      </ComponentErrorBoundary>
    );
  }
);

AddOpenGovReport.displayName = 'AddOpenGovReport';
